import React from "react";
import ReactDOM from "react-dom/client";
import reportWebVitals from "./reportWebVitals";
import "bootstrap/dist/css/bootstrap.min.css";
import App from "./main-component/App/App";
import "swiper/css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./css/fontawesome.css";
import "./css/themify-icons.css";
import "./css/animate.css";
import "./css/mousecursor.css";
import "./css/imageRevealHover.css";
import "./css/custom-fonts.css";
import "./css/main.css";
import "./css/design-system.css";

// i18n initialization
import "./i18n";

// Currency provider
import { CurrencyProvider } from "./context/CurrencyContext";

// Consent provider (cookie consent)
import { ConsentProvider } from "./context/ConsentContext";

// SEO: HelmetProvider for per-page meta tags
import { HelmetProvider } from "react-helmet-async";

// Scroll reveal observer — animate elements as they enter viewport
if (typeof window !== "undefined" && "IntersectionObserver" in window) {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
  );

  const observeElements = () => {
    document.querySelectorAll(".reveal-on-scroll, .stagger-children").forEach((el) => {
      observer.observe(el);
    });
  };

  if (document.readyState === "complete") {
    observeElements();
  } else {
    window.addEventListener("load", observeElements);
  }

  // Re-observe on DOM changes (SPA route transitions)
  const mutationObserver = new MutationObserver(() => {
    document.querySelectorAll(".reveal-on-scroll:not(.revealed), .stagger-children:not(.revealed)").forEach((el) => {
      observer.observe(el);
    });
  });
  mutationObserver.observe(document.body, { childList: true, subtree: true });
}

const rootElement = document.getElementById("root") as HTMLElement;

const AppTree = (
  <React.StrictMode>
    <HelmetProvider>
      <ConsentProvider>
        <CurrencyProvider>
          <App />
        </CurrencyProvider>
      </ConsentProvider>
    </HelmetProvider>
  </React.StrictMode>
);

// Always render fresh on the client.
// Prerendered HTML stays in place for bots/crawlers (SEO) but the React tree
// re-mounts on user browsers to avoid hydration mismatch errors (React #418)
// that fire when stateful contexts (i18n, ConsentContext, ReferralTracker,
// URL-param parsing, locale detection) compute different values in Puppeteer
// vs the user's browser. Clear the prerendered DOM first so React doesn't
// briefly show both.
if (rootElement.hasChildNodes()) {
  rootElement.innerHTML = "";
}
const root = ReactDOM.createRoot(rootElement);
root.render(AppTree);

reportWebVitals();
